import * as React from 'react';
import classNames from 'classnames';
import { capitalize, useScrollLock } from '@/helpers';
import { ButtonRegular } from '../Button';
import { Icon } from '../Icon';
import styles from './Modal.module.scss';

export const modalSizes = ['sm', 'md', 'fullscreen'] as const;

export type ModalSize = (typeof modalSizes)[number];

export interface ModalProps extends React.PropsWithChildren {
	readonly open: boolean;
	readonly size?: ModalSize;
	readonly contentCentered?: boolean;
	readonly onClose: () => void;
	readonly translations?: {
		readonly close: string;
	};
}

const defaultTranslations = {
	close: 'Close',
};

// TODO: fix issue with scroll lock not being removed when modal is closed by keyboard

/**
 * Generic Modal component, used for displaying modals with any content inside.
 */
export const Modal: React.FunctionComponent<ModalProps> = ({
	open,
	size = 'md',
	contentCentered = false,
	onClose,
	translations = defaultTranslations,
	children,
}) => {
	const dialogRef = React.useRef<HTMLDialogElement>(null);
	const setScrollLock = useScrollLock();

	React.useEffect(() => {
		if (open && !dialogRef.current?.open) {
			dialogRef.current?.showModal();
			setScrollLock(true);
			return;
		}

		if (!open && dialogRef.current?.open) {
			dialogRef.current?.close();
			setScrollLock(false);
			return;
		}
	}, [open, setScrollLock]);

	const handleClick = React.useCallback(
		(event: React.MouseEvent<HTMLDialogElement>): void => {
			const modal = event.currentTarget;
			const modalBox = modal.getBoundingClientRect();

			const clickedOutsideModal =
				event.clientY < modalBox.top ||
				event.clientY > modalBox.bottom ||
				event.clientX < modalBox.left ||
				event.clientX > modalBox.right;

			if (clickedOutsideModal) {
				onClose();
			}
		},
		[onClose],
	);

	const handleClose = React.useCallback(
		(event: React.SyntheticEvent<HTMLDialogElement>): void => {
			event.preventDefault();
			onClose();
		},
		[onClose],
	);

	return (
		// Required in order to implement the click away behavior. Escape is
		// still available for accessibility.
		// eslint-disable-next-line jsx-a11y/click-events-have-key-events, jsx-a11y/no-noninteractive-element-interactions
		<dialog
			ref={dialogRef}
			className={classNames(styles.wrapper, styles[`size${capitalize(size)}`])}
			onClick={handleClick}
			onClose={handleClose}
		>
			<ButtonRegular
				className={styles.closeButton}
				variant="ghost"
				hasOnlyIcon
				aria-label={translations.close}
				onClick={onClose}
			>
				<Icon name="close" size="md" />
			</ButtonRegular>
			<div
				className={classNames(styles.content, styles[`size${capitalize(size)}`], {
					[styles.contentCentered]: contentCentered,
				})}
			>
				{children}
			</div>
		</dialog>
	);
};
